import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { TlsCertificate, TlsRegister } from '../api/types'
import { CRITICALITY_LABEL } from '../pages/Services'
import { localeCorrente, t } from '../i18n/lingua'
import { Card, EmptyState } from './common'
import { AvvisiDelCertificato } from './NotificheCertificati'
import type { Column } from './DataTable'
import { DataTable } from './DataTable'

/**
 * Sicurezza → Cifratura: the register of the TLS certificates the machines
 * expose (tls_inventory_service), as three views — every installation, the
 * expiries, the self-signed ones — and the detail of one certificate: the CI
 * and the program exposing it, the business services standing on it, and the
 * certificate itself (subject, names, issuing CA and chain, validity, key,
 * signature, protocol, fingerprint).
 */

export type VistaCertificati = 'anagrafica' | 'scadenze' | 'autofirmati'

const data = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(localeCorrente()) : '—')

export function statoCertificato(c: TlsCertificate) {
  const g = c.days_left
  if (c.status === 'expired') return <span className="badge badge--error">{t('scaduto da {n} giorni', { n: -(g ?? 0) })}</span>
  if (c.status === 'expiring') return <span className="badge badge--warn">{t('scade fra {n} giorni', { n: g ?? 0 })}</span>
  if (c.status === 'soon') return <span className="badge badge--info">{t('scade fra {n} giorni', { n: g ?? 0 })}</span>
  if (c.status === 'ok') return <span className="badge badge--ok">{t('valido ancora {n} giorni', { n: g ?? 0 })}</span>
  return <span className="badge badge--muted">{t('scadenza non letta')}</span>
}

function Sottofiltri<T extends string>({ valori, attivo, onScegli }: {
  valori: Array<[T, string, number]>; attivo: T; onScegli: (v: T) => void
}) {
  return (
    <div className="toolbar" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
      {valori.map(([v, etichetta, n]) => (
        <button key={v} type="button" className={attivo === v ? '' : 'secondary'} onClick={() => onScegli(v)}>
          {etichetta} ({n})
        </button>
      ))}
    </div>
  )
}

export function RegistroCertificati({ dati, vista }: { dati: TlsRegister; vista: VistaCertificati }) {
  const [aperto, setAperto] = useState<TlsCertificate | null>(null)
  const [scadenza, setScadenza] = useState<'scaduti' | 'entro30' | 'entro90' | 'tutti'>('scaduti')
  const [autofirmati, setAutofirmati] = useState<'esposti' | 'locali' | 'tutti'>('esposti')

  let righe = dati.items
  if (vista === 'scadenze') {
    righe = righe.filter((c) =>
      scadenza === 'scaduti' ? c.status === 'expired'
        : scadenza === 'entro30' ? c.status === 'expiring'
          : scadenza === 'entro90' ? c.status === 'expiring' || c.status === 'soon'
            : true)
  } else if (vista === 'autofirmati') {
    righe = righe.filter((c) => c.self_signed
      && (autofirmati === 'tutti' || (autofirmati === 'locali' ? c.local_only : !c.local_only)))
  }

  const colonne: Column<TlsCertificate>[] = [
    { key: 'cn', header: t('Certificato'), value: (c) => `${c.cn} ${c.sans.join(' ')}`, render: (c) => (
      <>
        <button type="button" className="link" style={{ fontWeight: 600, textAlign: 'left' }} onClick={() => setAperto(c)}>{c.cn || '—'}</button>
        {c.sans.length > 0 && <><br /><small className="text-muted">{c.sans.slice(0, 3).join(', ')}{c.sans.length > 3 ? ` +${c.sans.length - 3}` : ''}</small></>}
      </>
    ) },
    { key: 'ci', header: t('CI e servizio'), value: (c) => `${c.ci.name} ${c.process ?? ''} ${c.port}`, render: (c) => (
      <>
        <Link to={`/cis/${c.ci.id}`}>{c.ci.name}</Link>
        <br /><small className="text-muted"><span className="mono">:{c.port}</span>{c.process ? ` · ${c.process}` : ''}{c.software ? ` · ${c.software}` : ''}</small>
        {c.services.length > 0 && <><br /><small>{c.services.join(', ')}</small></>}
      </>
    ) },
    { key: 'issuer', header: t('Emittente (CA)'), value: (c) => c.issuer_cn, render: (c) => (
      c.self_signed ? <span className="badge badge--warn">{t('autofirmato')}</span> : <small>{c.issuer_cn}</small>
    ) },
    { key: 'days_left', header: t('Scadenza'), value: (c) => c.days_left ?? 99999, filter: false, render: (c) => (
      <>{statoCertificato(c)}<br /><small className="text-muted">{data(c.not_after)}</small></>
    ) },
    { key: 'key', header: t('Chiave e protocollo'), value: (c) => `${c.key_type ?? ''} ${c.tls_version ?? ''}`, render: (c) => (
      <>
        <small>{[c.key_type && `${c.key_type}${c.key_bits ? ` ${c.key_bits}` : ''}`, c.tls_version].filter(Boolean).join(' · ') || '—'}</small>
        {c.weaknesses.length > 0 && <><br /><small style={{ color: 'var(--error)' }}>{c.weaknesses.join(', ')}</small></>}
      </>
    ) },
    { key: 'installations', header: t('Installazioni'), value: (c) => c.installations, filter: false },
  ]

  return (
    <>
      {vista === 'scadenze' && (
        <Sottofiltri attivo={scadenza} onScegli={setScadenza} valori={[
          ['scaduti', t('Scaduti'), dati.expired],
          ['entro30', t('Entro 30 giorni'), dati.expiring],
          ['entro90', t('Entro 90 giorni'), dati.expiring + dati.soon],
          ['tutti', t('Tutti, per scadenza'), dati.total],
        ]} />
      )}
      {vista === 'autofirmati' && (
        <>
          <p className="field__hint" style={{ marginTop: 0 }}>
            {t('Certificati autofirmati: non sono verificati da una CA attendibile, quindi non garantiscono l’identità del servizio. Sui servizi raggiungibili dalla rete è consigliato sostituirli con certificati emessi da una CA interna o pubblica; sui servizi accessibili solo localmente il rischio è inferiore.')}
          </p>
          <Sottofiltri attivo={autofirmati} onScegli={setAutofirmati} valori={[
            ['esposti', t('Raggiungibili da altre macchine'), dati.self_signed_exposed],
            ['locali', t('Solo in locale'), dati.self_signed - dati.self_signed_exposed],
            ['tutti', t('Tutti'), dati.self_signed],
          ]} />
        </>
      )}
      <Card>
        {righe.length === 0 ? (
          <EmptyState title={vista === 'scadenze' ? t('Nessun certificato in questa fascia') : vista === 'autofirmati'
            ? t('Nessun certificato autofirmato') : t('Nessun certificato letto')}
            hint={t('I certificati li legge l’agente CENSO, con una stretta di mano TLS verso ogni porta in ascolto della sua macchina.')} />
        ) : (
          <DataTable rows={righe} rowKey={(c) => c.key} columns={colonne}
            initialSort={{ key: 'days_left', dir: 'asc' }} conteggio={{ nome: t('certificati') }} paginazione={{ predefinita: 50 }} />
        )}
      </Card>
      {aperto && <DettaglioCertificato c={aperto} tutti={dati.items} onChiudi={() => setAperto(null)} />}
    </>
  )
}

function Riga({ etichetta, children }: { etichetta: string; children: ReactNode }) {
  return (
    <tr>
      <th style={{ textAlign: 'left', fontWeight: 500, color: 'var(--text-muted)', width: 180, verticalAlign: 'top' }}>{etichetta}</th>
      <td style={{ wordBreak: 'break-word' }}>{children}</td>
    </tr>
  )
}

/** One certificate: where it is and who exposes it, then what it says. */
export function DettaglioCertificato({ c, tutti, onChiudi }: { c: TlsCertificate; tutti: TlsCertificate[]; onChiudi: () => void }) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onChiudi() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onChiudi])
  const altrove = tutti.filter((x) => x.sha256 && x.sha256 === c.sha256 && x.key !== c.key)
  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={t('Dettaglio del certificato')}>
      <div className="dob-modal__scrim" onClick={onChiudi} />
      <div className="dob-modal__pannello" style={{ width: 'min(860px, 96vw)', maxHeight: '90vh' }}>
        <div className="dob-modal__testata">
          <div>
            <h3 className="dob-modal__titolo">{c.cn || t('Certificato')}</h3>
            <p className="dob-modal__sotto">{statoCertificato(c)} {c.self_signed && <span className="badge badge--warn">{t('autofirmato')}</span>}</p>
          </div>
          <div className="dob-modal__comandi">
            <button type="button" className="secondary" onClick={onChiudi}>{t('Chiudi')}</button>
          </div>
        </div>
        <div className="dob-modal__corpo">
          <h4 style={{ margin: '4px 0' }}>{t('Dove è esposto')}</h4>
          <table><tbody>
            <Riga etichetta={t('CI')}><Link to={`/cis/${c.ci.id}`}>{c.ci.name}</Link>{c.ci.address && <small className="text-muted"> · {c.ci.address}</small>}{c.ci.ci_class && <small className="text-muted"> · {c.ci.ci_class}</small>}</Riga>
            <Riga etichetta={t('Servizio che lo espone')}>
              <span className="mono">{c.address ? `${c.address}:` : ':'}{c.port}</span>
              {c.process && <> · {c.process}</>}{c.instance && <small className="text-muted"> ({c.instance})</small>}
              {c.software && <><br /><small>{c.software}</small></>}
              {c.local_only && <><br /><small className="text-muted">{t('in ascolto solo in locale')}</small></>}
            </Riga>
            <Riga etichetta={t('Business service')}>
              {c.services.length ? c.services.join(', ') : '—'}
              {c.criticality && <small className="text-muted"> · {t('criticità')} {CRITICALITY_LABEL[c.criticality] ?? c.criticality}</small>}
            </Riga>
            <Riga etichetta={t('Lo stesso certificato altrove')}>
              {altrove.length === 0 ? '—' : altrove.map((x) => (
                <div key={x.key}><Link to={`/cis/${x.ci.id}`}>{x.ci.name}</Link> <span className="mono text-muted">:{x.port}</span>{x.process ? ` · ${x.process}` : ''}</div>
              ))}
            </Riga>
            <Riga etichetta={t('Mappa Software')}><Link to={`/software-flow?ci=${c.ci.id}`}>{t('Apri il flusso applicativo del CI')}</Link></Riga>
            <Riga etichetta={t('Avvisi inviati')}><AvvisiDelCertificato chiave={c.key} /></Riga>
            <Riga etichetta={t('Sostituzione')}>
              <Link to={`/certificates?${new URLSearchParams({ tab: 'nuova', cn: c.cn, sans: c.sans.join(','), ci: c.ci.id, replaces: c.sha256 }).toString()}`}>
                {t('Rinnova con la CA')}
              </Link>
            </Riga>
          </tbody></table>

          <h4 style={{ margin: '14px 0 4px' }}>{t('Certificato')}</h4>
          <table><tbody>
            <Riga etichetta={t('Soggetto')}><span className="mono">{c.subject || '—'}</span></Riga>
            <Riga etichetta={t('Nomi alternativi (SAN)')}>{c.sans.length ? <span className="mono">{c.sans.join(', ')}</span> : '—'}</Riga>
            <Riga etichetta={t('Emittente (CA)')}><span className="mono">{c.issuer || '—'}</span>{c.self_signed && <><br /><small className="text-muted">{t('autofirmato: l’emittente è il certificato stesso')}</small></>}</Riga>
            <Riga etichetta={t('Catena presentata')}>
              {c.chain.length === 0 ? <small className="text-muted">{t('nessun certificato intermedio inviato dal server')}</small>
                : <ol style={{ margin: 0, paddingLeft: 18 }}>{c.chain.map((x, i) => <li key={i}><span className="mono">{x}</span></li>)}</ol>}
            </Riga>
            <Riga etichetta={t('Numero di serie')}><span className="mono">{c.serial || '—'}</span></Riga>
            <Riga etichetta={t('Valido dal')}>{data(c.not_before)}</Riga>
            <Riga etichetta={t('Valido fino al')}>{data(c.not_after)} {statoCertificato(c)}</Riga>
          </tbody></table>

          <h4 style={{ margin: '14px 0 4px' }}>{t('Crittografia')}</h4>
          <table><tbody>
            <Riga etichetta={t('Chiave')}>{c.key_type ? `${c.key_type}${c.key_bits ? ` ${c.key_bits} bit` : ''}` : '—'}</Riga>
            <Riga etichetta={t('Firma')}>{c.signature || '—'}</Riga>
            <Riga etichetta={t('Protocollo e cifrario')}>{[c.tls_version, c.cipher].filter(Boolean).join(' · ') || '—'}</Riga>
            <Riga etichetta={t('Debolezze')}>{c.weaknesses.length ? <span style={{ color: 'var(--error)' }}>{c.weaknesses.join(', ')}</span> : '—'}</Riga>
            <Riga etichetta={t('Impronta SHA-256')}><span className="mono" style={{ fontSize: 12 }}>{c.sha256 || '—'}</span></Riga>
          </tbody></table>
        </div>
      </div>
    </div>
  )
}
